import { Console } from 'console-feed';
import React, { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { Button } from '@mm/components';
import { ArrayItemType } from '@mm/utils';
import { chatWidgetTroubleshootingLastResultSelector } from '@mm/state/selectors/chatWidgetTroubleshootingSelectors';
import { Result } from '../ChatWidgetTroubleshootingToolResult';

const decodeArg = (arg: any) => {
  try {
    switch (arg.type) {
      case 'string':
      case 'number':
        return arg.value;
      case 'boolean':
        return Boolean(arg.value);
      case 'object':
        if (arg.preview?.properties?.length > 0) {
          const result: any = {};
          for (const property of arg.preview.properties) {
            result[property.name] = decodeArg(property);
          }
          return result;
        }
        return arg.description ?? `[${arg.value}]`;
      default:
        return `[${arg.type}]`;
    }
  } catch (err) {
    return '[unknown]';
  }
};
export const useConsoleResult = (): Result => {
  const result = useSelector(chatWidgetTroubleshootingLastResultSelector);
  const [showConsole, setShowConsole] = useState(false);

  const { logs, warningCount, errorCount } = useMemo(() => {
    if (!result) {
      return {};
    }

    let warningCount = 0;
    let errorCount = 0;

    const logs = result.consoleEvents.map(event => {
      if (event.type === 'error') {
        errorCount += 1;
      }
      if (event.type === 'warning') {
        warningCount += 1;
      }
      return {
        id: String(event),
        method: event.type,
        data: event.args.map(decodeArg),
      } as ArrayItemType<Console['props']['logs']>;
    });
    return { logs, warningCount, errorCount };
  }, [result]);

  if (!result || result.widgets?.fbCustomerChat || result.widgets?.webchat) {
    return;
  }
  if (
    !logs ||
    logs.length === 0 ||
    warningCount === undefined ||
    errorCount === undefined
  ) {
    return {
      result: 'ok',
      description: 'No console errors or warnings',
    };
  }

  const console = (
    <>
      <Button
        color="transparent"
        css={{
          container: 'chat-widget-troubleshooting-tool-result__toggle-button',
        }}
        onClick={() => setShowConsole(s => !s)}
      >
        {showConsole ? 'Hide' : 'Show'} Logs
      </Button>
      {showConsole && (
        <div className="chat-widget-troubleshooting-tool-result__console">
          <Console logs={logs} variant="dark" />
        </div>
      )}
    </>
  );

  if (warningCount === 0 && errorCount === 0) {
    return {
      result: 'ok',
      description: (
        <>
          No console errors or warnings
          {console}
        </>
      ),
    };
  }
  return {
    result: 'info',
    description: (
      <>
        Console: {errorCount} errors, {warningCount} warnings
        {console}
      </>
    ),
  };
};
